import curry from "../lib/core/functional/curry";
import thunk from "../lib/core/functional/thunk";

import createQueue from "./simpleQueue";
import { listenToConnectionChanges } from "./connectionHandler";

const DEFAULT_LIMITS = {
    "4g": 5,
    "3g": 3,
    "2g": 2,
    "slow-2g": 2,
};

const COMPOSED_LIMITS = {
    ...DEFAULT_LIMITS,
    ...(window.RM_EXT_DL_LIMIT || {}),
};

const { request, release, changeSettings, onEmpty } = createQueue(COMPOSED_LIMITS["4g"]);

const loadedResources = {};
const loadWaiters = {};

const connectionMap = {
    "4g": () => changeSettings(COMPOSED_LIMITS["4g"]),
    "slow-2g": () => changeSettings(COMPOSED_LIMITS["slow-2g"]),
    "2g": () => changeSettings(COMPOSED_LIMITS["2g"]),
    "3g": () => changeSettings(COMPOSED_LIMITS["3g"]),
};

onEmpty(function onDoneLoadingResources() {
    const event = new CustomEvent("externalresourcesqueue:finished");
    window.dispatchEvent(event);
});

listenToConnectionChanges(function onConnectionInfoChanged(effectiveType) {
    (connectionMap[effectiveType] || connectionMap["4g"])();
});

const createLoadCallback = curry(function createLoadCallback(resourceID, callback, event) {
    if (resourceID) {
        release(resourceID);
    }
    callback(event);
});

const loadElement = curry((element, callback, resourceID) => {
    element.addEventListener("load", createLoadCallback(resourceID, callback));
    element.addEventListener("error", createLoadCallback(resourceID, callback));
    document.head.insertBefore(element, document.head.firstChild);
});

function hasLoaded(selector) {
    return localStorage.getItem(`rmEx-${selector}`) === "1";
}

function storeLoadedResource(selector) {
    loadedResources[selector] = true;
    localStorage.setItem(`rmEx-${selector}`, "1");
}

const runCallback = curry(function runCallback(selector, callbackIndex, callbackPair) {
    const callback = callbackPair[callbackIndex];

    if (!callback) {
        console.log(`Callback for index ${callbackIndex} not found in pair.`);
        return;
    }

    try {
        callback();
    } catch (error) {
        console.error(`Error on user JS after loading ${selector}`);
        console.error(error);
        console.error(callback);
    }
});

const runQueue = thunk((selector, event) => {
    if (event instanceof ErrorEvent || event.type === "error") {
        loadWaiters[selector].forEach(runCallback(selector, 1));
    } else {
        storeLoadedResource(selector);
        loadWaiters[selector].forEach(runCallback(selector, 0));
    }
    loadWaiters[selector].length = 0;
});

const makeCallback = curry((selector, event) => {
    setTimeout(runQueue(selector, event), 1);
});

export default function loadExternalResource(selector, element, callback, onError) {
    const callbackPair = [callback, onError];

    if (selector in loadedResources) {
        runCallback(selector, 0, callbackPair);
        return;
    }

    if (selector in loadWaiters) {
        loadWaiters[selector].push(callbackPair);
        return;
    }

    const existingResource = document.querySelector(selector);
    if (existingResource) {
        storeLoadedResource(selector);
        runCallback(selector, 0, callbackPair);
        return;
    }

    loadWaiters[selector] = [callbackPair];

    const load = loadElement(element, makeCallback(selector));

    if (hasLoaded(selector)) {
        load(false);
    } else {
        request().then(load);
    }
}
